import { FunctionComponent, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as yup from "yup";
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SearchableInputsDropdown from '../../components/common/SearchableDropdown';
import alertIcon from "../../assets/dropdown/alert-icon-error.svg";
import InputsNew from '../../components/common/InputsNew';
import DateSelector from '../../components/common/DateSelector';
import { addDays, addHours, addMinutes, format, isAfter, isBefore, parseISO, setHours, setMinutes } from 'date-fns';
import { showError, showSuccess } from '../../components/common/ToastNotification';
import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GOAL_BY_STATIC_GOAL_ID, GOAL_DETAILS, GOAL_MASTER_DATA, TASK_BY_TASK_ID, GET_ALL_SKILLS } from '../../api/query';
import Loader from '../../components/common/Loader';
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import { CREATE_GOAL } from '../../api/mutation';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCumb from '../../components/common/Breadcrumb';
import backArrow from "../../assets/task/back_arrow.svg";
import InputsDropdownMultipleSearchSelect from '../../components/common/InputsDropdownMultipleSearchSelect';
// }

const EditTask: FunctionComponent = () => {
    const { userInfo } = useAuth()
    const navigate = useNavigate();
    const employeeID = userInfo?.employee_id || '';

    const location = useLocation()
    const state = location.state as {
        task: any
    };

    const { task } = state

    //api
    const {
        data: goalsData,
        loading: goalsDataLoading,
        error: goalsDataError
    } = useQuery(GOAL_MASTER_DATA, {
        variables: { goal_for: null },
        fetchPolicy: 'no-cache',
    });

    const {
        data: taskData,
        loading: taskDataLoading,
        error: taskDataError
    } = useQuery(TASK_BY_TASK_ID, {
        variables: { task_id: parseInt(task?.id) },
        skip: !task,
        fetchPolicy: 'no-cache',
    });

    const { data: skillData, loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
        fetchPolicy: 'no-cache',
    });

    const [
        getExistingGoals,
        { data: existingGoalsData, loading: existingGoalsDataLoading },
    ] = useLazyQuery(GOAL_BY_STATIC_GOAL_ID, {
        fetchPolicy: 'no-cache'
    });

    const [createGoal, { loading: createGoalLoading }] = useMutation(CREATE_GOAL, {
        fetchPolicy: 'no-cache',
    })

    const [
        getGoalDetails,
        { data: goalData, loading: goalDataLoading },
    ] = useLazyQuery(GOAL_DETAILS, {
        fetchPolicy: 'no-cache'
    });

    const {
        project_name: projectName,
        project_id: projectID,
        goal_name: goalName,
        goal_static_type_name: goalType,
        goal_id: goalID,
        goal_start_date: startGoalDate,
        goal_end_date: endGoalDate,
        project_members: projectMembers,
        goals_static_id: goalStaticID,
        static_task_type_name: taskType,
        tasks_static_id: taskTypeID,
        title: taskName,
        start_datetime: taskStrtDate,
        hours: taskHours,
        mins: taskMins,
        checklist_items: taskchecklistItems,
        assigned_to_name: assignedToName,
        dependency_task_id: dependencyTaskID,
        dependency_task_name: dependencyTaskName
        // goal_tasks: goalTasks
    } = taskData?.task_by_task_id || {};

    const [taskTypeOptions, setTaskTypeOptions] = useState<{ key: string; name: string }[]>([]);
    const [assignedMemberOptions, setAssignedMemberOptions] = useState<{ key: string; name: string }[]>([]);
    const [parentTaskOptions, setParentTaskOptions] = useState<{ key: string; name: string }[]>([]);
    const [taskChecklist, setTaskChecklist] = useState<{ key: string; name: string }[]>([]);
    const [clearSelected, setClearSelected] = useState(false);
    const [clearMemberSelected, setClearMemberSelected] = useState(false);

    const [endDate, setEndDate] = useState<string | null>(null);
    const [endDateTime, setEndDateTime] = useState<string | null>(null);

    // Skills state
    const [skillOptions, setSkillOptions] = useState<{ key: string; name: string }[]>([]);
    const [selectedSkill, setSelectedSkill] = useState<string>(''); // Single skill name
    const [selectedSkillID, setSelectedSkillID] = useState<string>(''); // Single skill ID
    const [goalSkills, setGoalSkills] = useState<any[]>([]);

    // Validation schema
    const addTaskSchema = yup.object().shape({
        taskType: yup.string().required('This field is required'),
        taskID: yup.string().required('This field is required'),
        taskName: yup.string()
            .test('not-only-whitespace', 'Please add a valid Task Name', value => !value || value.trim().length > 0)
            .required('This field is required'),
        assignedMember: yup.string(),
        assignedMemberID: yup.string(),
        taskChecklist: yup
            .array()
            .of(yup.string())
            .min(1, 'At least one task must be selected'),
        startTaskDate: yup
            .string()
            .nullable(),
        durationDays: yup.number().typeError("").required("").min(0, "Days cannot be negative"),
        durationTimeHour: yup
            .number()
            .typeError("")
            .required("")
            .min(0, "Hours should be between 0 to 23")
            .max(23, "Hours should be between 0 to 23"),
        durationTimeMinute: yup
            .number()
            .typeError("")
            .required("")
            .min(0, "Minutes should be between 0 to 59")
            .max(59, "Minutes should be between 0 to 59"),
        parentTask: yup.string().nullable(),
        parentTaskID: yup.string().nullable(),
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<any>({
        resolver: yupResolver(addTaskSchema),
        defaultValues: {
            assignToOther: false
        }
    });

    //watch
    const watchAssignedMember: string = watch('assignedMember')
    const watchTaskType: string = watch('taskType');
    const selected = watch('taskChecklist');
    const watchParentTask: string = watch('parentTask');

    // Skills useEffect - populate skill options
    useEffect(() => {
        if (skillData && skillData.get_all_skills?.skills?.length > 0) {
            const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
                key: skill.id,
                name: skill?.skill_name
            }));
            setSkillOptions(options);
        }
    }, [skillData]);

    useEffect(() => {
        if (projectID && goalStaticID) {
            getExistingGoals({
                variables: {
                    static_goal_id: parseInt(goalStaticID),
                    project_id: parseInt(projectID)
                },
            })
        }

        if (goalID) {
            getGoalDetails({
                variables: {
                    goal_id: parseInt(goalID)
                }
            })
        }
    }, [projectMembers, employeeID])

    useEffect(() => {
        if (goalsData && goalType) {
            const selectedGoal = goalsData.goal_master_data.goals_static.find((goal: any) => goal.name === goalType);

            if (selectedGoal) {
                const tasks = selectedGoal.tasks.map((task: any) => ({
                    key: task.id,
                    name: task.name,
                    type: task?.role_name
                }));
                setTaskTypeOptions(tasks);
                setClearSelected(true)
            }

            setValue("taskType", taskType);
            setValue("taskID", taskTypeID);
            setValue('startTaskDate', taskStrtDate?.split("T")[0])
            setValue('durationTimeHour', taskHours)
            setValue('durationTimeMinute', taskMins)
            setValue("parentTaskID", dependencyTaskID)
            setValue("parentTask", dependencyTaskName)

            if (goalStaticID == "55") {
                // task name = task type name, checklists populated via watchTaskType effect
                setValue('taskName', taskType);
            } else {
                setValue('taskName', taskName);
            }
        }
    }, [goalsData, goalType]);

    useEffect(() => {
        if (goalType && watchTaskType) {
            const selectedGoal = goalsData.goal_master_data.goals_static.find((goal: any) => goal.name === goalType);
            const selectedTask = selectedGoal?.tasks.find((task: any) => task.name === watchTaskType);

            if (selectedTask) {
                const checklists = selectedTask.checklists.map((item: any) => ({
                    key: parseInt(item.id),
                    name: item.name
                }));
                setTaskChecklist(checklists);

                if (goalStaticID == "55") {
                    setValue("taskName", watchTaskType);
                    setValue("taskChecklist", checklists.map((item: any) => item.key));
                } else {
                    const validChecklistIDs = checklists?.map((item: any) => item.key);
                    const selectedChecklistIDs = taskchecklistItems?.map((item: any) => parseInt(item.checklist.id))
                        .filter((id: any) => validChecklistIDs.includes(id));
                    setValue("taskChecklist", selectedChecklistIDs);
                }

                setClearSelected(false);
            } else {
                setTaskChecklist([]);
            }
        }
    }, [watchTaskType]);

    useEffect(() => {
        const startDate = watch("startTaskDate");
        // const hour = Number(watch("startTimeHour"));
        // const minute = Number(watch("startTimeMinute"));
        // const period = watch("startTimePeriod");

        const durationDays = Number(watch("durationDays")) || 0;
        const allowedDurationHours = durationDays * 9
        const allowedDurationMinutes = allowedDurationHours * 60
        const durationHours = Number(watch("durationTimeHour")) || 0;
        const convertedDurationHours = durationHours * 60
        const durationMinutes = Number(watch("durationTimeMinute")) || 0;

        const totalDurationMinutes = convertedDurationHours + durationMinutes

        if (totalDurationMinutes > allowedDurationMinutes) {
            showError(`Total duration cannot exceed ${allowedDurationHours} hours for the given number of days.`);
            setValue("durationTimeHour", 0);
            setValue("durationTimeMinute", 0);
            return;
        }

        // if (durationDays < 1) {
        //     showError("Duration days cannot be less than 1");
        //     setValue("durationDays", 1);
        //     return;
        // } else if (durationDays > 30) {
        //     showError("Duration days cannot be more than 30.");
        //     setValue("durationDays", 0);
        //     return;
        // }

        // if (durationHours > allowedDurationHours) {
        //     showError("Duration hours cannot be more than 9 Hours for a day.");
        //     setValue("durationTimeHour", 0);
        //     return;
        // }

        if (durationMinutes > 59) {
            showError("Duration minute cannot be more than 59.");
            setValue("durationTimeMinute", 0);
            return;
        }

        // Check if we have a valid start date and start time
        if (
            startDate &&
            // !isNaN(hour) &&
            // !isNaN(minute) &&
            // period &&
            (durationDays || durationHours || durationMinutes)
        ) {
            // let parsedHour = hour % 12;
            // if (period === "PM") parsedHour += 12;

            // Parse date parts manually to avoid UTC timezone shift
            const [year, month, day] = startDate.split('-').map(Number);
            const startDateObj = new Date(year, month - 1, day, 0, 0, 0, 0);

            // Add durations
            let endDateObj = addMinutes(startDateObj, durationMinutes);
            endDateObj = addHours(endDateObj, durationHours);
            endDateObj = addDays(endDateObj, (durationDays - 1));

            // Get goal limits
            const goalStart = new Date(watch("startGoalDate"));
            const goalEnd = new Date(watch("endGoalDate"));
            goalStart.setHours(0, 0, 0, 0);
            goalEnd.setHours(23, 59, 59, 999);

            // Check if endDate is within goal bounds
            if (endDateObj < goalStart || endDateObj > goalEnd) {
                showError("End date is outside goal date range.");
                setEndDate(null);
                setEndDateTime(null);
                return;
            }

            setEndDate(format(endDateObj, "MM-dd-yyyy"));
            setEndDateTime(format(endDateObj, "hh:mm a"));
        } else {
            setEndDate(null);
            setEndDateTime(null);
        }
    }, [
        watch("startTaskDate"),
        // watch("startTimeHour"),
        // watch("startTimeMinute"),
        // watch("startTimePeriod"),
        watch("durationDays"),
        watch("durationTimeHour"),
        watch("durationTimeMinute"),
    ]);

    useEffect(() => {
        if (existingGoalsData && existingGoalsData.goals_by_static_goal_id[0]?.goals.length > 0) {
            const goal = existingGoalsData?.goals_by_static_goal_id[0]?.goals.find((g: any) => g.id == goalID);
            const goalTasks = goal?.tasks?.filter((t: any) => t.id != task?.id)?.map((t: any) => ({
                key: t.id,
                name: t.title
            }));
            setParentTaskOptions(goalTasks);
        }
    }, [existingGoalsData])

    const goalMembers = goalData?.goal_details?.goal_details?.goal_members
    const goalDescription = goalData?.goal_details?.goal_details?.description

    useEffect(() => {
        if (goalData && goalData?.goal_details?.goal_details?.goal_members?.length > 0) {
            const goalMembers = goalData?.goal_details?.goal_details?.goal_members
            // Map members to assignedMemberOptions format
            const memberOptions = goalMembers?.map((member: any) => ({
                key: member.employee_id,
                name: member.member_name,
            }));

            setAssignedMemberOptions(memberOptions);

            // Find if the default employeeID exists in the members list
            const defaultMember = memberOptions?.find((member: any) => member.key == employeeID);

            if (defaultMember) {
                // Set default assigned member
                setValue('assignedMemberID', defaultMember.key);
                setValue('assignedMember', defaultMember.name);
                trigger('assignedMember');
            } else {
                // Clear if employeeID not found in members
                setValue('assignedMemberID', '');
                setValue('assignedMember', '');
            }
        } else {
            // If no members or project not found, reset the options
            setAssignedMemberOptions([]);
        }

        // Handle goal skills from goal data
        if (goalData?.goal_details?.goal_details?.skills?.length > 0) {
            const goalSkillOptions = goalData?.goal_details?.goal_details?.skills?.map((s: any) => ({
                key: s?.skill_id,
                name: s?.skill_name
            }));
            setGoalSkills(goalSkillOptions);
        }
    }, [goalData])

    // Handle task skill from task data (single skill)
    useEffect(() => {
        if (taskData?.task_by_task_id?.skills?.length > 0) {
            // Get the first skill (single selection)
            const firstSkill = taskData.task_by_task_id.skills[0];
            setSelectedSkill(firstSkill?.skill_name || '');
            setSelectedSkillID(firstSkill?.skill_id || '');
        }
    }, [taskData]);

    // useEffect(() => {
    //     const gT = goalTasks?.map((t: any) => ({
    //         key: t.id,
    //         name: t.title
    //     }));
    //     setParentTaskOptions(gT);
    // }, [goalTasks])

    function handleTaskType(key: number, name: string): void {
        setValue('taskID', key)
        setValue('taskType', name)
        trigger('taskType')
    }

    function handleSelectAll(): void {
        const allTasks = taskChecklist.map(task => task.key);
        setValue('taskChecklist', allTasks);
    }

    function handleDeselectAll(): void {
        setValue('taskChecklist', []);
    };

    function handleSelectParentTask(key: number, name: string): void {
        setValue('parentTask', name)
        setValue('parentTaskID', key)
        trigger('parentTask')
    }

    const handleKeyDown = (event: any) => {
        const { key } = event;
        // Allow control keys like backspace, delete, arrows, etc.
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "Tab" ||
            key === "Enter"
        ) {
            return;
        }

        // Prevent non-numeric keys
        if (!/^[0-9]$/.test(key)) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const paste = event.clipboardData.getData("text");
        if (!/^\d+$/.test(paste)) {
            event.preventDefault();
        }
    };

    // Skill handler function (single selection)
    const handleSelectSkill = (key: any, name: any) => {
        setSelectedSkill(name);
        setSelectedSkillID(key.toString());
    };

    async function onSubmit(data: any): Promise<void> {
        // Validate skills
        if (!selectedSkillID) {
            showError('Please select at least one skill');
            return;
        }
        // Get all tasks for this goal from goalData
        const allGoalTasks = goalData?.goal_details?.goal_details?.tasks || [];

        // Simulate the task assignment change
        const updatedTasks = allGoalTasks.map((t: any) => {
            if (t.id === task?.id) {
                // This is the task being edited - update its assignee
                return { ...t, assigned_to_id: parseInt(data.assignedMemberID) };
            }
            return t;
        });

        // Count tasks per member after the change
        const memberTaskCounts: { [memberId: string]: number } = {};
        updatedTasks.forEach((t: any) => {
            const assigneeId = t.assigned_to_id?.toString();
            if (assigneeId) {
                memberTaskCounts[assigneeId] = (memberTaskCounts[assigneeId] || 0) + 1;
            }
        });

        // Only include members who have at least one task
        const goalMembersWithTasks = goalMembers?.filter((member: any) => {
            const memberId = member.employee_id?.toString();
            return memberTaskCounts[memberId] > 0;
        });

        const goalMembersIds = goalMembersWithTasks?.map((member: any) => {
            return member?.employee_id
        });

        // Validate that at least one member has tasks
        if (!goalMembersIds || goalMembersIds.length === 0) {
            showError('Cannot save: No members would have tasks assigned after this change');
            return;
        }

        // if (goalMembersIds.length < 2) {
        //     showError('Please assign tasks to at least two members before saving');
        //     return
        // }


        const checklists =
            selected?.length > 1
                ? selected.map((checklist_id: number) => ({
                    task_id: parseInt(data.taskID),
                    checklist_id,
                    status: false,
                    created_by: parseInt(employeeID),
                }))
                : {
                    task_id: parseInt(data.taskID),
                    checklist_id: selected[0],
                    status: false,
                    created_by: parseInt(employeeID),
                };


        const ed = endDate && format(endDate, "yyyy-MM-dd")

        // Compute removed members (original members not in current goal_members)
        const removedMembers = goalMembers
            .filter((member: any) => !goalMembersIds.includes(member.employee_id))
            .map((member: any) => member.employee_id);


        // Prepare skill (single selection)
        const skillIds = selectedSkillID ? [parseInt(selectedSkillID)] : [];

        const inputGoalData = {
            goal_title: goalName,
            project_id: parseInt(projectID),
            static_goal_id: parseInt(goalStaticID),
            is_new_goal: false,
            goal_start_date: startGoalDate,
            goal_end_date: endGoalDate,
            created_by: goalData?.goal_details?.goal_details?.created_by,
            existing_goal_id: parseInt(goalID),
            goal_members: goalMembersIds,
            removed_member_ids: removedMembers,
            description: goalDescription,
            tasks: {
                is_new_task: false,
                existing_task_id: parseInt(task?.id),
                goal_id: parseInt(goalID),
                static_goal_id: parseInt(goalStaticID),
                static_task_id: parseInt(data.taskID),
                task_title: data.taskName,
                task_start_date: `${data.startTaskDate}T00:00:00.000Z`,
                task_end_date: `${ed}T23:59:59.000Z`,
                dependency_task: data?.parentTaskID ? parseInt(data.parentTaskID) : null,
                // task_end_date: `${ed}T${endDateTime?.split(" ")[0]}:00.000Z`,
                created_by: parseInt(employeeID),
                days: data.durationDays,
                hours: data.durationTimeHour,
                mins: data.durationTimeMinute,
                checklists: checklists,
                assigned_to: parseInt(data.assignedMemberID),
                skill_ids: skillIds,
            }
        }

        try {
            const response = await createGoal({ variables: { goal_data: inputGoalData } });
            if (response.data.create_goal.status_code === 200) {
                showSuccess(response.data.create_goal.message)
                navigate(-1)
            }
            if (response.data.create_goal.status_code === 400) {
                showError(response.data.create_goal.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="editask-section-dv w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb clientName={assignedToName} />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Edit Task</h3>
                    </div>
                    <div className="self-stretch rounded-lg  bg-dark-white-item-blg flex flex-col items-start justify-start gap-6  p-8 mq575:p-3 ">
                        <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full inputbg-blog-item">
                            <InputsNew
                                label="Project Name*"
                                type='text'
                                contentPlaceholder={projectName}
                                disabled={true}
                            />
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start gap-[16px]">
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                <div className="flex flex-row items-center justify-start gap-[6px]">
                                    <input
                                        className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                        type="radio"
                                        value="existing"
                                        {...register('goal')}
                                        id="existingGoal"
                                        checked={true}
                                        disabled={true}
                                    />
                                    <label htmlFor="existingGoal" className={`cursor-pointer relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block min-w-[50px]`}>
                                        Existing Goal
                                    </label>
                                </div>
                                <div className="flex flex-row items-center justify-start gap-[6px]">
                                    <input
                                        className="cursor-pointer m-0 h-4 w-4 relative overflow-hidden shrink-0"
                                        type="radio"
                                        value="new"
                                        {...register('goal')}
                                        id="newGoal"
                                        disabled={true}
                                    />
                                    <label htmlFor="newGoal" className={`cursor-pointer relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block min-w-[50px]`}>
                                        New Goal
                                    </label>
                                </div>
                            </div>
                            {errors?.goal && (
                                <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                    <img src={alertIcon} alt="" />
                                    {typeof errors.goal.message === 'string' ? errors.goal.message : 'Invalid input'}
                                </p>
                            )}

                            {/* {errors?.goal && <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg"><img src={alertIcon} alt="" />{errors?.goal.message}</p>} */}
                        </div>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                label="Goal Type*"
                                type='text'
                                contentPlaceholder={goalType}
                                disabled={true}
                            />
                            <InputsNew
                                label="Goal Name/Label*"
                                type='text'
                                contentPlaceholder={goalName}
                                disabled={true}
                            />
                            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                                <Controller
                                    name="startGoalDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date*"
                                            selectedDate={startGoalDate}
                                            onChange={(date: string | null) => {
                                                field.onChange(date);
                                                setValue('startGoalDate', date);
                                                trigger('startGoalDate');
                                            }}
                                            error={errors.startGoalDate?.message}
                                            nothideParsedValue={true}
                                            disabled={true}
                                        // maxDate={new Date()}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                                <Controller
                                    name="endGoalDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="End Date*"
                                            selectedDate={endGoalDate}
                                            onChange={(date: string | null) => {
                                                field.onChange(date);
                                                setValue('endGoalDate', date);
                                                trigger('endGoalDate');
                                            }}
                                            error={errors.endGoalDate?.message}
                                            nothideParsedValue={true}
                                            disabled={true}
                                        // maxDate={new Date()}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder={goalDescription ? goalDescription : "Goal Description"}
                                    label="Goal Description"
                                    type="textarea"
                                    // register={register('goalDescription')}
                                    // errorMessage={errors.goalDescription}
                                    disabled={true}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm gap-2">
                            <label className="self-stretch relative font-semibold dark:text-white">Goal Members</label>
                            <div className="self-stretch flex flex-row items-center justify-start flex-wrap content-center py-0 pl-0 gap-x-[5.8px] gap-y-2  mq1425:box-border">
                                {goalMembers?.length > 0 && (
                                    goalMembers?.map((gm: any) => {
                                        return (
                                            <div className="adminsetting_blg_dv round-btn-blg-dv cursor-pointer [border:none] customlight_gray_bg py-spacing-1 px-spacing-3 rounded-81xl flex flex-row items-center justify-center bg-text-text-tertiary border-solid border-stroke hover:bg-gainsboro-300">
                                                {gm?.member_name}
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>
                        {/* <div className="flex min-w-full selectall_header_bg">
                            <input
                                type="checkbox"
                                id="goalDescriptionCheckbox"
                                
                            />
                            <label htmlFor="goalDescriptionCheckbox" className="font-semibold">Goal Description</label>
                        </div> */}
                        <h5 className="my-0 font-bold text-xl-dv dark:text-white">Task Details</h5>
                        <div className="self-stretch flex flex-col items-start justify-start gap-4">
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                {/* <SearchableInputsDropdown
                                    label="Task Type*"
                                    initialValue={watchTaskType}
                                    placeHolder="Select Task Type"
                                    options={taskTypeOptions}
                                    onSelect={handleTaskType}
                                    errorMessage={errors?.taskType}
                                    clearSelected={clearSelected}
                                /> */}

                                <InputsDropdownMultipleSearchSelect
                                    label="Task Type*"
                                    initialValue={watchTaskType}
                                    placeHolder="Select Task Type"
                                    options={taskTypeOptions}
                                    onSelect={handleTaskType}
                                    inputType="radio"
                                    errorMessage={errors?.taskType}
                                    clearSelected={clearSelected}
                                />
                                <InputsNew
                                    contentPlaceholder="Task Label/Name*"
                                    label="Task Name*"
                                    type="text"
                                    register={register('taskName')}
                                    errorMessage={errors.taskName}
                                    disabled={goalStaticID == "55"}
                                />
                                <SearchableInputsDropdown
                                    label="Assigned Member*"
                                    initialValue={watchAssignedMember}
                                    placeHolder="Select Member"
                                    options={assignedMemberOptions}
                                    onSelect={(key: number, name: string) => {
                                        setClearMemberSelected(false);
                                        setValue('assignedMemberID', key);
                                        setValue('assignedMember', name);
                                        trigger('assignedMember');
                                    }}
                                    errorMessage={errors?.assignedMember}
                                    clearSelected={clearMemberSelected}
                                />
                                <SearchableInputsDropdown
                                    label="Parent Task"
                                    initialValue={watchParentTask}
                                    placeHolder="Select Parent Task"
                                    options={parentTaskOptions}
                                    onSelect={handleSelectParentTask}
                                    errorMessage={errors?.parentTask}
                                />
                            </div>
                            {taskChecklist && taskChecklist.length > 0 && (
                                <div className="grid gap-[8px] w-full py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                    <div className="self-stretch flex flex-row items-center justify-start flex-col py-0 pr-0 mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border gap-[8px]">
                                        <div className="self-stretch flex items-center justify-start py-0  gap-[8px] ">
                                            <h6 className="my-0 relative dark:text-white whitespace-pre-wrap"><b>Select Task Checklist*</b></h6>
                                            <span className="checkliste-selected-count">{selected?.length}</span>
                                        </div>
                                        <div className="flex min-w-full selectall_header_bg">
                                            <input
                                                type="checkbox"
                                                id="selectAllCheckbox"
                                                checked={taskChecklist.length > 0 && taskChecklist.every(task => control._formValues.taskChecklist?.includes(task.key))}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSelectAll();
                                                    } else {
                                                        handleDeselectAll();
                                                    }
                                                }}
                                                disabled={goalStaticID == "55"}
                                            />
                                            <label htmlFor="selectAllCheckbox" className="font-semibold">Select All</label>
                                        </div>
                                    </div>
                                    <Controller
                                        control={control}
                                        name="taskChecklist"
                                        render={({ field }) => (
                                            <div className="grid grid-cols-2 mq575:grid-cols-1 gap-2">
                                                {taskChecklist?.map((task, index) => {
                                                    const isChecked = field.value?.includes(task?.key);
                                                    return (
                                                        <div className="taskchecklist_dv">
                                                            <label
                                                                key={task?.key}
                                                                className={`py-2 px-2 border flex items-start gap-1 rounded cursor-pointer ${isChecked ? 'border-purple-500 bg-white active_taskchecklist_dv' : 'border-gray-300'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    value={task?.key}
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        if (goalStaticID == "55") return;
                                                                        const updated = isChecked
                                                                            ? field.value.filter((t: string) => t !== task?.key)
                                                                            : [...field.value, task?.key];
                                                                        field.onChange(updated);
                                                                    }}
                                                                    disabled={goalStaticID == "55"}
                                                                // className="hidden"
                                                                />
                                                                {task?.name}
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    />
                                    {errors.taskChecklist && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {typeof errors.taskChecklist.message === 'string' ? errors.taskChecklist.message : 'Invalid input'}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Task Skill Section (Single Selection) */}
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                <SearchableInputsDropdown
                                    label="Task Skill"
                                    initialValue={selectedSkill}
                                    placeHolder={goalSkills.length === 0 ? "No goal skills available" : "Choose"}
                                    options={goalSkills}
                                    onSelect={handleSelectSkill}
                                />
                            </div>

                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full select-scheduler-custom-hei">

                                {/* Start Date */}
                                <Controller
                                    name="startTaskDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date"
                                            selectedDate={field.value}
                                            onChange={async (date: string | null | any) => {
                                                const start = new Date(startGoalDate);
                                                const end = new Date(endGoalDate);
                                                const current = new Date(date);

                                                if (current < start) {
                                                    showError("Start date cannot be before the goal start date.");
                                                    return;
                                                }

                                                if (current > end) {
                                                    showError("Start date cannot be after the goal end date.");
                                                    return;
                                                }

                                                field.onChange(date);
                                                setValue("startTaskDate", date);
                                                trigger("startTaskDate");
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            error={errors.startTaskDate?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />

                                {/* Duration */}
                                <div className="flex items-start justify-start gap-[8px]">
                                    {/* Days */}
                                    <div className="flex-1 flex flex-col items-start justify-start max-w-[131px] scheduler-shift-pt-dv">
                                        <InputsNew
                                            label={"Number of Days*"}
                                            contentPlaceholder={"DD"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onPaste={handlePaste}
                                            register={{ ...register("durationDays") }}
                                            errorMessage1={errors.durationDays}
                                            autoComplete={"off"}
                                            defaultValue={1}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                                <div className="flex items-end justify-start gap-[8px] w-full total_estimated_label">
                                    <div className="flex-1 flex flex-col items-start justify-start max-w-[131px] pt-[19px]">
                                        <InputsNew
                                            label={"Total Estimated Time*"}
                                            contentPlaceholder={"HH"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onPaste={handlePaste}
                                            register={{ ...register("durationTimeHour") }}
                                            errorMessage1={errors.durationTimeHour}
                                            autoComplete={"off"}
                                        />
                                    </div>

                                    {/* Minutes */}
                                    <div className="flex-1 flex flex-col items-start justify-start max-w-[131px] pt-[19px]">
                                        <InputsNew
                                            label={""}
                                            contentPlaceholder={"MM"}
                                            type="number"
                                            maxValue={2}
                                            onKeyDown={handleKeyDown}
                                            onPaste={handlePaste}
                                            register={{ ...register("durationTimeMinute") }}
                                            errorMessage1={errors.durationTimeMinute}
                                            autoComplete={"off"}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* End Date and Time */}
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                                <div className="flex flex-col items-start justify-start gap-[8px]">
                                    <div className="self-stretch relative text-sm font-semibold font-body-reg text-text-text-primary text-left dark:text-white">
                                        End Date
                                    </div>
                                    <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-center justify-start py-spacing-1-and-half px-0">
                                        <div className="flex-1 flex flex-row items-center justify-start">
                                            <div className="flex-1 relative text-sm font-body-reg text-text-text-primary text-left dark:text-white">
                                                {endDate ? endDate : "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

            </form>
            {(goalsDataLoading || createGoalLoading || taskDataLoading || existingGoalsDataLoading || goalDataLoading || skillLoading) && (
                <Loader />
            )}
        </>
    );
};

export default EditTask;
